'use client';

import { useMemo, useState } from 'react';
import { Icon } from '../../../components/icons';
import { NavLink } from '../../../components/ui/NavLink';
import { Input } from '../../../components/ui/Input';
import { cn } from '../../../lib/utils';
import {
  isAuthenticatedSession,
  useAuthSession,
} from '../../auth/hooks/useAuthSession';
import {
  canAccessSettingsNavItem,
  settingsGroupLabels,
  settingsNavItems,
  type SettingsNavItem,
} from './settings-nav-config';

function navItemClassName(isActive: boolean): string {
  return cn(
    'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium outline-none transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
    isActive
      ? 'bg-[var(--color-actions-selected-light-mode)] text-foreground'
      : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
  );
}

function splitByGroups(items: SettingsNavItem[]) {
  const grouped = {
    user: items.filter((item) => item.group === 'user'),
    friend: items.filter((item) => item.group === 'friend'),
    legal: items.filter((item) => item.group === 'legal'),
  };
  return grouped;
}

export function SettingsSideNav() {
  const { data: session } = useAuthSession();
  const [query, setQuery] = useState('');

  const visibleItems = useMemo(() => {
    if (!isAuthenticatedSession(session)) return [];
    const q = query.trim().toLowerCase();

    return settingsNavItems
      .filter((item) =>
        canAccessSettingsNavItem(
          item.audience,
          session.data.user.role,
          session.data.user.shelterRole,
          session.data.user.shelterId,
        ),
      )
      .filter((item) => (q ? item.label.toLowerCase().includes(q) : true));
  }, [query, session]);

  const grouped = splitByGroups(visibleItems);

  return (
    <div className='flex h-full min-h-0 flex-col rounded-2xl border border-border bg-card p-3 sm:p-4'>
      <h2 className='font-heading text-3xl font-semibold tracking-tight text-foreground'>
        Settings
      </h2>

      <div className='relative mt-4'>
        <Icon
          name='search-outline'
          className='pointer-events-none absolute left-3.5 top-1/2 size-5 -translate-y-1/2 text-muted-foreground'
        />
        <Input
          id='settings-search'
          type='search'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder='Search settings'
          aria-label='Search settings'
          className='rounded-xl bg-background pl-11'
        />
      </div>

      <nav
        className='mt-4 min-h-0 flex-1 space-y-5 overflow-auto'
        aria-label='Settings navigation'
      >
        {(['user', 'friend'] as const).map((groupKey) => {
          const items = grouped[groupKey];
          if (!items.length) return null;
          return (
            <section key={groupKey} className='space-y-2'>
              <h3 className='px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                {settingsGroupLabels[groupKey]}
              </h3>
              <ul className='space-y-1'>
                {items.map((item) => (
                    <li key={item.id}>
                      <NavLink
                        href={item.href}
                        className={({ isActive }) => navItemClassName(isActive)}
                      >
                        <Icon name={item.icon} className='size-5 shrink-0' />
                        <span className='truncate'>{item.label}</span>
                      </NavLink>
                    </li>
                  ))}
              </ul>
            </section>
          );
        })}
      </nav>

      {grouped.legal.length > 0 ? (
        <div className='mt-4 border-t border-border pt-3'>
          {grouped.legal.map((item) => (
              <NavLink
                key={item.id}
                href={item.href}
                className={({ isActive }) =>
                  cn(
                    'flex w-full items-center gap-3 rounded-xl border border-border px-3 py-2.5 text-sm font-medium outline-none transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
                    isActive
                      ? 'bg-(--color-actions-selected-light-mode) text-foreground'
                      : 'bg-background text-muted-foreground hover:bg-accent/40 hover:text-foreground',
                  )
                }
              >
                <Icon name={item.icon} className='size-5 shrink-0' />
                <span className='truncate'>{item.label}</span>
              </NavLink>
            ))}
        </div>
      ) : null}
    </div>
  );
}
